import React, { useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Table, { IndeterminateCheckbox } from 'components/Table';
import { ENUM_SURVEY_STATUS } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import { useClickOutside } from 'hooks/useClickOutside';
import AppRoutes from 'routes/routes';
import { convertStatusReport, getAgeFromMonthAndYear } from 'utils/helper';

const ControlEvaluationAnalysisItem = ({ item }) => {
  const navigator = useNavigate();

  const [openControlSurveyItem, setIsOpenControlSurveyItem] = useState(false);
  const controlSurveyItemRef = useRef(null);
  const contentControlSurveyItemRef = useRef(null);

  useClickOutside(controlSurveyItemRef, () => setIsOpenControlSurveyItem(false));

  const handleDetailUser = () => {
    navigator(AppRoutes.usersDetailFn(item.child_id), {
      state: { reportId: item.id, evaluationId: item.evaluation_id },
      replace: false,
    });
  };

  return (
    <div className="flex-between-center text-sm">
      <div className="relative flex items-center">
        <button type="button" className="w-6 h-8" onClick={() => setIsOpenControlSurveyItem((prev) => !prev)} ref={controlSurveyItemRef}>
          <img src={require('assets/resources/MoreVert.svg').default} alt="more vert icon" width={24} height={24} />
        </button>
        <div
          ref={contentControlSurveyItemRef}
          className={`absolute z-2 right-0 py-1 bg-white shadow-soft-md animation-dropdown ${openControlSurveyItem ? 'show' : ''}`}
        >
          <ul>
            <li
              onClick={() => handleDetailUser()}
              className="px-3 w-48 py-1.25 text-black-102 text-sm whitespace-nowrap hover:bg-white-795 cursor-pointer"
            >
              評価結果の入力
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const EvaluationAnalysisTable = ({ data, setSort, reloadData, sort, page, keyword, isFilter, onPageChange }) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.language);

  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);

  const getDataNoPublic = (table) => {
    const rows = table.getRowModel().rows;
    const result = _.filter(rows, (item) => item.original.status !== ENUM_SURVEY_STATUS.PUBLIC);
    return result;
  };

  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        // Get item index with page number
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        // Create new object and push to array
        if (pageSelectedExists === -1) {
          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        /**
         * If you are select
         * - Delete the selected item from the array
         * - Delete all Object contains lines
         * - Add new object
         */
        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = selectedRowWithPage[pageSelectedExists].selected;
        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: [...rowSelected, rowSelect.original.id],
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const dataNoPublic = getDataNoPublic(table);
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          return [
            ...prevState,
            {
              page: page,
              selected: dataNoPublic?.map((row) => row?.original?.id),
            },
          ];
        }

        /**
         * If the Select of the page is currently in the Testhall (Condition) state
         * (Condition): Select length is equal to the number of non -public status items on the current page
         * then remove the whole object of that page to become Uncheck.
         */
        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === dataNoPublic?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        selectedRowWithPage.splice(pageSelectedExists, 1);
        selectedRowWithPage.push({
          page: page,
          selected: dataNoPublic.map((row) => row?.original?.id),
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={`${getDataNoPublic(table)?.length <= 0 && '!cursor-not-allowed'} flex justify-center items-center w-4`}>
            <IndeterminateCheckbox
              className={`m-auto  ${getDataNoPublic(table)?.length <= 0 && '!pointer-events-none'}`}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === getDataNoPublic(table)?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <div className="flex justify-center">
              <IndeterminateCheckbox
                className={`m-auto ${row.original.status !== ENUM_SURVEY_STATUS.PUBLIC ? '' : 'opacity-50 !cursor-not-allowed'}`}
                {...{
                  checked:
                    row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC
                      ? _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id)
                      : false,
                  indeterminate: false,
                  onChange: () => row?.original?.status !== ENUM_SURVEY_STATUS.PUBLIC && handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'child_id',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span className="whitespace-nowrap text-nowrap">子供ID</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() || '-'}</p>;
        },
        size: 120,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'user_email',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>メールアドレス</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() || '-'}</p>;
        },
        size: 125,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'child_name',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>名前</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() || '-'}</p>;
        },
        size: 142,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'child_age',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>年齢</span>;
        },
        cell: ({ row, getValue }) => {
          const age = getAgeFromMonthAndYear(row?.original?.child_age_month, row?.original?.child_age_year, t, currentLanguage);
          return <p className="whitespace-nowrap text-nowrap">{age}</p>;
        },
        size: 88,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'child_gender',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>性別</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() || '-'}</p>;
        },
        size: 88,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'status',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>状態</span>;
        },
        cell: ({ row, getValue }) => {
          return (
            <div className="flex">
              <span
                className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusReport(getValue())?.class}`}
              >
                {convertStatusReport(getValue())?.name}
              </span>
            </div>
          );
        },
        size: 88,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'sdq_created_at',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>アンケート</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.sdq_created_at;
          return <p>{value ? `済(${dayjs(value).format(FORMAT_TIME.DATE)})` : '-'}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'ase_created_at',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>動画アップ</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.ase_created_at;
          return <p>{value ? `済(${dayjs(value).format(FORMAT_TIME.DATE)})` : '-'}</p>;
        },
        size: 144,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: 'admin_name',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>担当者</span>;
        },
        cell: ({ row, getValue }) => {
          return <p>{getValue() || '-'}</p>;
        },
        size: 160,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'ase_expired_at',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>期限</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.ase_expired_at;
          return <p>{value ? dayjs(value).format(FORMAT_TIME.DATE) : '-'}</p>;
        },
        size: 120,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'eval_created_at_1',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>評価結果</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.eval_created_at;
          return <p>{value ? dayjs(value).format(FORMAT_TIME.DATE) : '-'}</p>;
        },
        size: 120,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'eval_created_at_2',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>運動動画</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.eval_created_at;
          return <p>{value ? dayjs(value).format(FORMAT_TIME.DATE) : '-'}</p>;
        },
        size: 120,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        id: 'eval_created_at_3',
        accessorKey: '-',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>評価動画</span>;
        },
        cell: ({ row, getValue }) => {
          const value = row?.original?.eval_created_at;
          return <p>{value ? dayjs(value).format(FORMAT_TIME.DATE) : '-'}</p>;
        },
        size: 120,
        sortingFn: () => { },
        sortDescFirst: true,
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        enableSorting: false,
        size: 60,
        cell: ({ row }) => <ControlEvaluationAnalysisItem item={row?.original || {}} />,
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword]);

  return (
    <>
      <Table columns={columns} data={data} isFilter={isFilter} isAnalysis={true} />
    </>
  );
};

export default React.memo(EvaluationAnalysisTable);
